import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// DesignerContextMenu — right-click menu for canvas elements. Visually
// matches the diary's .dv2-context-menu (same shadow, radius, padding,
// item heights) so the app reads consistently across surfaces.
//
// Closes on: Esc, outside click, or any item click. Positions itself
// at (x, y) relative to the viewport; flips to the left/up if it would
// otherwise spill off-screen.
//
// Props:
//   x, y       — viewport coords where the menu should open
//   items      — array of { label, onClick, danger?, disabled?, divider? }
//                A `divider: true` entry renders as a separator row
//                instead of an action.
//   onClose    — () => void

const DesignerContextMenu = ({ x, y, items = [], onClose }) => {
  const ref = useRef(null);

  // Close on Esc + outside click.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // pointerdown so it fires before the next item's click handler
    window.addEventListener('pointerdown', onDoc, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDoc, true);
    };
  }, [onClose]);

  // Position the menu after mount so we can read its dimensions and
  // flip if it would spill past the viewport edge. Without this the
  // menu would sometimes appear cut off when right-clicking near the
  // bottom or right edge of the screen.
  const [pos, setPos] = React.useState({ left: x, top: y, ready: false });
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - 8)  left = window.innerWidth  - rect.width  - 8;
    if (top  + rect.height > window.innerHeight - 8) top = window.innerHeight - rect.height - 8;
    if (left < 8) left = 8;
    if (top  < 8) top = 8;
    setPos({ left, top, ready: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y]);

  const menu = (
    <div
      ref={ref}
      className="v2-cd-context-menu"
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        if (item.divider) {
          return <div key={`d-${i}`} className="v2-cd-context-divider" />;
        }
        return (
          <button
            key={item.label}
            type="button"
            className={`v2-cd-context-item ${item.danger ? 'v2-cd-context-item--danger' : ''}`}
            disabled={!!item.disabled}
            onClick={() => { item.onClick(); onClose(); }}
            role="menuitem"
          >
            {item.label}
            {item.shortcut && <span className="v2-cd-context-shortcut">{item.shortcut}</span>}
          </button>
        );
      })}
    </div>
  );

  return ReactDOM.createPortal(menu, document.body);
};

export default DesignerContextMenu;
